import Vue from 'vue';
import defaultClient from '@/utils/vuex/client';
import { endpointUrls } from '@/utils/constants/twEndpoints';
import isPDFExporterAgent from '@/utils/helpers/isPDFExporterAgent';
import { logError } from '@/utils/helpers/logger.utility';

const productParam = 'spaces';

// 408 Request Timeout and 429 Too Many Requests are 4xx but mean "try again
// later", so they stay retryable rather than being treated as permanent.
const RETRYABLE_STATUSES = new Set([408, 429]);

// Marks a failure the page editor should not keep retrying: launchpad refused
// the request (a 4xx), e.g. personal API keys are restricted by the account's
// security settings. Anything else may be temporary.
function markIfPermanent(error) {
  const status = error && error.response && error.response.status;
  if (status >= 400 && status < 500 && !RETRYABLE_STATUSES.has(status)) {
    // eslint-disable-next-line no-param-reassign
    error.isPermanent = true;
  }
  return error;
}

export default {
  namespaced: true,
  state: {
    items: {},
    collaborativeEditingApiKey: undefined,
  },

  getters: {
    apiKeys: (state) =>
      Object.values(state.items).sort((keyA, keyB) => keyA.name.localeCompare(keyB.name)),
  },

  mutations: {
    updateApiKeys(state, { apiKeys }) {
      const apiKeysObj = apiKeys.reduce((partial, item) => ({ ...partial, [item.id]: item }), {});
      state.items = { ...state.items, ...apiKeysObj };
    },

    removeApiKeys(state, { apiKeyIds }) {
      if (apiKeyIds.length) {
        apiKeyIds.forEach((id) => Vue.delete(state.items, id.toString()));
      }
    },

    regenerateApiKey(state, { newApiKey, oldApiKey }) {
      const newitems = { ...state.items };
      delete newitems[oldApiKey.id];
      newitems[newApiKey.id] = newApiKey;
      state.items = newitems;
    },
    setCollaborativeEditingKey(state, apiKey) {
      state.collaborativeEditingApiKey = apiKey;
    },
  },

  actions: {
    async fetchApiKeys({ dispatch, commit }, params) {
      // Bypass request as it cannot be proxied
      if (process.env.NODE_ENV === 'development' && process.env.VUE_APP_PROXY_TARGET) {
        return [];
      }

      if (isPDFExporterAgent()) {
        return [];
      }

      const args = params || {};
      return defaultClient.get(endpointUrls.apiKeys, {
        params: {
          product: productParam,
          showHidden: args.showHidden || false,
        },
      })
        .then((response) => {
          const { data: { apiKeys } } = response;
          const apiKeysCount = apiKeys.length;
          if (apiKeysCount) {
            commit('updateApiKeys', { apiKeys, apiKeysCount });
          }
          return response;
        })
        .catch((error) => {
          dispatch('alerts/showAlert', { type: 'error', message: 'Failed to get API keys' }, { root: true });
          return Promise.reject(error);
        });
    },

    create({ dispatch, commit }, { data, shouldRegenerate = false, showAlert = true }) {
      return defaultClient.post(endpointUrls.apiKeys, {
        scope: 'spaces',
        ...data,
      })
        .then((response) => {
          const { data: { apiKey } } = response;

          if (!shouldRegenerate) {
            commit('updateApiKeys', {
              apiKeys: [apiKey],
            });
            if (showAlert) {
              dispatch('alerts/showAlert', { type: 'success', message: 'API key created successfully' }, { root: true });
            }
          }
          return response;
        })
        .catch((error) => {
          if (showAlert) {
            dispatch('alerts/showAlert', { type: 'error', message: 'Error creating API key' }, { root: true });
          }
          Promise.reject(error);
        });
    },

    // Returns what the page editor's real-time connection logs in with: a
    // single-use collaboration ticket, so the browser never holds an API key.
    // A ticket is fetched for every connection, since each one works once.
    // Only when launchpad answers that tickets are disabled for the
    // installation is a personal API key used instead. Any failure is thrown,
    // never retried with an API key; see markIfPermanent.
    async fetchCollaborativeEditingToken({ dispatch }) {
      // Bypass request as it cannot be proxied
      if (process.env.NODE_ENV === 'development' && process.env.VUE_APP_PROXY_TARGET) {
        return process.env.VUE_APP_PROXY_API_KEY;
      }

      try {
        const { data } = await defaultClient.post(endpointUrls.collaborationTickets, {
          scope: 'spaces',
        });

        if (data.status === 'disabled') {
          return await dispatch('fetchCollaborativeEditingApiKeyToken');
        }
        if (typeof data.ticket !== 'string' || data.ticket === '') {
          throw new Error('Launchpad returned no collaboration ticket');
        }
        return data.ticket;
      } catch (error) {
        logError(error);
        throw markIfPermanent(error);
      }
    },

    // The personal API key used while collaboration tickets are disabled for the
    // installation. It is reused until it expires, as before tickets.
    async fetchCollaborativeEditingApiKeyToken({ state, commit }) {
      const apiKey = state.collaborativeEditingApiKey;
      if (apiKey && new Date() < new Date(apiKey.dateValidUntil)) {
        return apiKey.accessToken;
      }

      const { data } = await defaultClient.post(endpointUrls.apiKeys, {
        scope: 'spaces',
        name: 'Collaborative Editing',
        validFor: 43200, // seconds (12 hours)
        hidden: true,
      });

      commit('setCollaborativeEditingKey', data.apiKey);
      return data.apiKey.accessToken;
    },
  },

  async revokeApiKeys({ commit, dispatch }, { apiKeys, resourceName }) {
    const promises = apiKeys.map(
      (key) => dispatch('destroyApiKey', key),
    );

    try {
      const response = await Promise.all(promises);
      const apiKeyIds = [];
      response.forEach((res, index) => {
        if (res.data.status === 'ok') {
          apiKeyIds.push(apiKeys[index].id);
        } else {
          dispatch('alerts/showAlert', {
            type: 'error',
            message: `Error revoking '${apiKeys[index].name} API key`,
          }, { root: true });
        }
      });

      commit('removeApiKeys', { apiKeyIds });
      const resource = resourceName !== undefined
        ? resourceName
        : 'All API keys';
      dispatch('alerts/showAlert', { type: 'success', message: `${resource} revoked successfully` }, { root: true });
      return response;
    } catch (error) {
      dispatch('alerts/showAlert', { type: 'error', message: 'Error revoking API keys' }, { root: true });
      return Promise.reject(error);
    }
  },

  async regenerateApiKey({ dispatch, commit }, { key, vnode }) {
    const { name, dateValidUntil, scope } = key;

    // The API is changing to return an array but the POST is still a string for now
    // TODO: send array in POST when API is changed.
    const scopeToPass = scope.join(',');

    try {
      await dispatch('destroyApiKey', key);
      const response = await dispatch('create', {
        data: {
          name,
          dateValidUntil,
          scope: scopeToPass,
        },
        shouldRegenerate: true,
      });
      const { data } = response;
      vnode.key = data.apiKey.id; //eslint-disable-line
      commit('regenerateApiKey', { newApiKey: data.apiKey, oldApiKey: key });
      dispatch('alerts/showAlert', {
        type: 'success',
        message: `API key '${name}' regenerated successfully`,
      }, { root: true });
      return data;
    } catch (error) {
      dispatch('alerts/showAlert', { type: 'error', message: `Error regenerating API key '${name}'` }, { root: true });
      return Promise.reject(error);
    }
  },

  /* eslint-disable-next-line no-empty-pattern */
  destroyApiKey({}, apiKey) {
    if (apiKey && apiKey.id && Number.isInteger(apiKey.id)) {
      const url = `/launchpad/v1/apikeys/${apiKey.id}.json`;
      return defaultClient.delete(url);
    }
    return false;
  },
};
